function decodeBasicHtmlEntities(s) {
  return String(s || '')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
}

function plainTextToParagraphs(text) {
  const lines = String(text || '')
    .split(/\n+/)
    .map((s) => s.trim())
    .filter(Boolean)
  if (!lines.length) return ''
  return lines.map((line) => `<p>${line}</p>`).join('')
}

function stripTagsForCompare(h) {
  return String(h || '')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function unwrapPreCodeBlock(raw) {
  const inner = decodeBasicHtmlEntities(String(raw || '')).trim()
  if (!inner) return ''
  if (/<\/?[a-z][\s\S]*>/i.test(inner)) return inner
  return plainTextToParagraphs(inner)
}

function unwrapPreCodeBlocks(h) {
  return String(h || '').replace(
    /<pre[^>]*>\s*<code[^>]*>([\s\S]*?)<\/code>\s*<\/pre>/gi,
    (_, raw) => unwrapPreCodeBlock(raw),
  )
}

/** 粘贴/误存时常见：上面已渲染一遍，下面又以代码块存同一段 HTML */
function dedupeRenderedAndCodeBlock(html) {
  let h = String(html || '')
  const preRe = /<pre[^>]*>\s*<code[^>]*>([\s\S]*?)<\/code>\s*<\/pre>/i
  const match = preRe.exec(h)
  if (!match) return h

  const before = h.slice(0, match.index).trim()
  const unwrapped = unwrapPreCodeBlock(match[1]).trim()
  if (!before || !unwrapped) return h

  const beforeText = stripTagsForCompare(before)
  const innerText = stripTagsForCompare(unwrapped)
  if (beforeText.length < 24 || innerText.length < 24) return h

  const shorter = Math.min(beforeText.length, innerText.length)
  const longer = Math.max(beforeText.length, innerText.length)
  const overlap =
    beforeText.includes(innerText) ||
    innerText.includes(beforeText) ||
    beforeText.slice(0, Math.min(80, shorter)) === innerText.slice(0, Math.min(80, shorter))

  if (!overlap && shorter / longer < 0.55) return h

  // 保留带样式的那份（通常来自代码块内的原始 HTML）
  return unwrapped
}

/**
 * 纠正误把正文 HTML 存成代码块/转义文本；加载编辑器与小程序展示前共用。
 */
export function normalizeRichHtmlBody(html) {
  let h = String(html || '').trim()
  if (!h) return ''

  if (/&lt;\/?[a-z]/i.test(h) && !/<\/?[a-z]/i.test(h)) {
    h = decodeBasicHtmlEntities(h)
  }

  h = dedupeRenderedAndCodeBlock(h)
  h = unwrapPreCodeBlocks(h)

  const escapedInP = h.match(/^<p[^>]*>([\s\S]*)<\/p>$/i)
  if (escapedInP && /&lt;\/?[a-z]/i.test(escapedInP[1])) {
    const inner = decodeBasicHtmlEntities(escapedInP[1]).trim()
    if (/<\/?(p|div|span|h[1-6]|ul|ol|li)\b/i.test(inner)) h = inner
  }

  return h.trim()
}

/** 含块级背景/版式时 WangEditor 可视化编辑会丢样式，应走 HTML 源码模式 */
export function richHtmlNeedsSourceMode(html) {
  const h = String(html || '')
  if (!h.trim()) return false
  return /<(?:div|section|table|tbody|tr|td|th)[^>]*\bstyle\s*=\s*["'][^"']*\b(?:background|padding|border-radius|max-width|margin|box-shadow)/i.test(
    h,
  )
}

function sanitizeImgInlineStyle(styleStr) {
  const parts = String(styleStr || '')
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean)
  const kept = []
  parts.forEach((decl) => {
    const idx = decl.indexOf(':')
    if (idx <= 0) return
    const key = decl.slice(0, idx).trim().toLowerCase()
    const val = decl.slice(idx + 1).trim()
    if (key === 'height' && val !== 'auto' && !/%$/i.test(val)) return
    if (key === 'min-height' || key === 'max-height') return
    kept.push(`${key}:${val}`)
  })
  ;['max-width:100%', 'height:auto', 'display:block', 'box-sizing:border-box'].forEach((rule) => {
    const key = rule.split(':')[0]
    if (!kept.some((d) => d.toLowerCase().startsWith(`${key}:`))) kept.unshift(rule)
  })
  return kept.join(';')
}

/** 去掉 135/秀米 图片写死的宽高，避免编辑区里比例被压扁/拉长 */
export function normalizeRichHtmlImages(html) {
  return String(html || '').replace(/<img\b([^>]*)>/gi, (_full, inner) => {
    let s = String(inner || '')
    s = s.replace(/\s+width\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    s = s.replace(/\s+height\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    if (/\bstyle\s*=/i.test(s)) {
      s = s.replace(/\bstyle\s*=\s*(["'])([\s\S]*?)\1/i, (_m, q, v) => {
        return `style=${q}${sanitizeImgInlineStyle(v)}${q}`
      })
    } else {
      s = `style="max-width:100%;height:auto;display:block;box-sizing:border-box;" ${s.trim()}`
    }
    return `<img ${s.trim()}>`
  })
}

/** WangEditor 加载前：强制把误存的代码块正文还原为可编辑富文本，避免 Slate 崩溃 */
export function normalizeRichHtmlForEditor(html) {
  let h = normalizeRichHtmlBody(html)
  h = normalizeRichHtmlImages(h)
  h = String(h || '').trim()
  if (!h) return '<p><br></p>'

  const plain = h
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim()
  if (!plain && !/<img\b/i.test(h)) return '<p><br></p>'
  return h
}

function resolvePreviewUrl(url, resolveUrl) {
  const u = String(url || '').trim()
  if (!u || u.startsWith('data:')) return u
  if (typeof resolveUrl === 'function') return resolveUrl(u) || u
  return u
}

/** 管理后台手机预览：补全图片地址并限制宽度 */
export function prepareRichHtmlForAdminPreview(html, resolveUrl) {
  let h = normalizeRichHtmlBody(html)
  if (!h) return ''

  h = h.replace(/\bsrc\s*=\s*"([^"]*)"/gi, (_, url) => {
    return `src="${resolvePreviewUrl(String(url || '').trim(), resolveUrl)}"`
  })
  h = h.replace(/\bsrc\s*=\s*'([^']*)'/gi, (_, url) => {
    return `src='${resolvePreviewUrl(String(url || '').trim(), resolveUrl)}'`
  })

  return normalizeRichHtmlImages(h)
}
